// docs/62 — a pet emote's wiggle as a pose over time, for the board (the DOM screens run the same
// shapes as CSS keyframes, emote.css `fk-pet-*`). Pure: `t` runs 0→1 over PET_EMOTE_MS. The pose
// is about the beast's feet, in art pixels, and never changes its frame (the commissioned beasts
// have only one).
import type { PetEmoteDef } from './catalogue';

export interface PetPose { sx: number; sy: number; dx: number; dy: number; angle: number }

export const REST: PetPose = { sx: 1, sy: 1, dx: 0, dy: 0, angle: 0 };

type Key = [t: number, pose: Partial<PetPose>];

/** Keyframes per mood (as emote.css), `lean` signed toward the ranger. */
function keys(mood: PetEmoteDef['key'], lean: number): Key[] {
  switch (mood) {
    case 'happy': return [[0, {}], [0.12, { dy: -5, sx: 0.96, sy: 1.06 }], [0.24, { sx: 1.08, sy: 0.92 }], [0.38, { dy: -5, sx: 0.96, sy: 1.06 }], [0.5, { sx: 1.06, sy: 0.94 }], [0.62, {}], [1, {}]];
    case 'alert': return [[0, {}], [0.14, { sx: 0.94, sy: 1.12 }], [0.3, { dy: -3, sx: 0.96, sy: 1.1 }], [0.42, { sx: 1.04, sy: 0.96 }], [0.7, { sx: 0.98, sy: 1.04 }], [1, {}]];
    case 'play': return [[0, {}], [0.15, { angle: -10, dx: -2 }], [0.32, { angle: 10, dx: 2 }], [0.49, { angle: -8, dx: -2 }], [0.66, { angle: 8, dx: 2 }], [0.83, { angle: -3 }], [1, {}]];
    case 'comfort': return [[0, {}], [0.3, { sx: 1.08, sy: 0.88, dx: 2 * lean, angle: 6 * lean }], [0.65, { sx: 1.06, sy: 0.9, dx: 3 * lean, angle: 8 * lean }], [1, {}]];
  }
}

const ease = (u: number) => u * u * (3 - 2 * u);

/** The pose at `t` (0–1) of a mood; at rest outside it. */
export function petPose(mood: PetEmoteDef['key'], t: number, lean: number): PetPose {
  if (!(t > 0 && t < 1)) return REST;
  const k = keys(mood, lean);
  let i = 0;
  while (i < k.length - 2 && t > k[i + 1]![0]) i++;
  const [t0, a] = k[i]!; const [t1, b] = k[i + 1]!;
  const u = ease(Math.min(1, Math.max(0, (t - t0) / (t1 - t0 || 1))));
  const f = (n: keyof PetPose) => (a[n] ?? REST[n]) + ((b[n] ?? REST[n]) - (a[n] ?? REST[n])) * u;
  return { sx: f('sx'), sy: f('sy'), dx: f('dx'), dy: f('dy'), angle: f('angle') };
}
